import React, { useEffect, useState } from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import LogoText from 'shared/assets/logoText.svg';

import EnterAccessCode from 'auth/components/EnterAccessCode';
import EnterEmail from 'auth/components/EnterEmail';
import ErrorBoundary from 'layout/components/ErrorBoundary';

import resetApp from 'auth/helpers/resetApp';
import routesConfig from 'constants/routesConfig.json';

import {
  DropdownMenu,
  DropdownMenuItem,
} from 'shared/components/dropdown/DropdownMenu';

import * as Styled from 'auth/styles/LoginPage.style';
import * as Text from 'theme/components/Text.style';

import {
  LANGUAGE_NATIVE_NAMES,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from 'i18n/languages';

export default function LoginPage(): JSX.Element {
  const { t } = useTranslation('pageLogin');
  const [language, setLanguage] = useState<SupportedLanguage>(() =>
    normalizeLanguage(i18n.language)
  );
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const isAccessCodePage = location.pathname === '/login/access-code';

  const selectLanguage = (code: SupportedLanguage): void => {
    setLanguage(code);
    i18n.changeLanguage(code);
  };

  const languageItems: DropdownMenuItem[] = SUPPORTED_LANGUAGES.map((code) => ({
    label: <Text.P2>{LANGUAGE_NATIVE_NAMES[code]}</Text.P2>,
    selected: code === language,
    onSelect: ({ close }) => {
      selectLanguage(code);
      close();
    },
  }));

  const helpClick = () => {
    history.push(routesConfig.FAQ_PAGE);
  };

  useEffect(() => {
    localStorage.removeItem('authorization');
  }, []);

  return (
    <Styled.Page>
      <Styled.LogoSection>
        <Styled.LogoButton onClick={resetApp}>
          <Styled.Logo src={LogoText} />
        </Styled.LogoButton>
      </Styled.LogoSection>
      <Styled.CardSection>
        <ErrorBoundary>
          {isAccessCodePage ? <EnterAccessCode /> : <EnterEmail />}
        </ErrorBoundary>
      </Styled.CardSection>
      <Styled.BottomSection>
        <DropdownMenu
          isOpen={isLanguageMenuOpen}
          onOpenChange={setIsLanguageMenuOpen}
          items={languageItems}
          usePortal
          renderTrigger={({ isOpen, toggle }) => (
            <Styled.BottomButton
              as="button"
              type="button"
              onClick={toggle}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-label={t('page.selectLanguage')}
            >
              <Text.P1Semibold>
                {LANGUAGE_NATIVE_NAMES[language]}
              </Text.P1Semibold>
            </Styled.BottomButton>
          )}
        />
        <Styled.BottomButton as="button" onClick={helpClick}>
          <Text.P1Semibold>{t('page.help')}</Text.P1Semibold>
        </Styled.BottomButton>
      </Styled.BottomSection>
    </Styled.Page>
  );
}
